/**
 * file used in Machinery Fuel tab in vessel configuration
 */
import {
  Col,
  Row,
  Button,
  Card,
  CardBody,
  Form,
  CardFooter,
  Input,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../..";
import { queryKeyes } from "shared/queryKeys";
import { FormikProps, useFormik } from "formik";
import FormValuesDebug from "utils/debugTools/FormValuesDebug";
import ErrorComponent from "Components/ErrorComponent";
import apiGlobal from "global/api.global";
import { formattooltip, handleServerResponse, isConfigurationButtonDisabled } from "GenericForms/Helper";
import ToolTip from "Components/ToolTip";
import { useEffect, useState } from "react";
import { queryClient } from "react-query/queryClient";
import { VesselConfigrationTabs } from "shared/constants";
import isEqual from "fast-deep-equal";
import Message from "Components/Message";
import FormLoading from "Components/FormLoading";
import { getConfigurationLimitsAction } from "Store/Generic/ReportingSlice";

interface FuelMachineryConfigurationType {
  refreshVesselMachineries: number;
  setRefreshVesselMachineries: (value: number) => void;
  setCheckValuesBeforeSwitch?: (value: boolean) => void;
  setTabName?: (value: string) => void;
  setFormik?: (value: FormikProps<any>) => void;
  VesselConfActiveTab: number;
  formik?: FormikProps<any>;
  refreshForm: boolean;
}

const FuelMachineryConfiguration = ({
  refreshVesselMachineries,
  setRefreshVesselMachineries,
  setCheckValuesBeforeSwitch,
  setTabName,
  setFormik,
  VesselConfActiveTab,
  refreshForm
}: FuelMachineryConfigurationType) => {
  /** State variables start */
  const { VesselID, Vessels, ConfigurationLimits, reportingLoading, reportingError } = useSelector((state: RootState) => state.Reporting);
  const vessel = Vessels.find((rec: any) => rec.id === VesselID);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const VesselMachinery = ConfigurationLimits?.vessel_machinery_fc_group || [];
  const FuelSetting = ConfigurationLimits?.vessel_fuel_setting || [];
  const FuelTypes = ConfigurationLimits?.vessel_fuel_information || [];
  const dispatch = useDispatch();
  /** State variables end */

  /** Assign initial values to fuel consumption Formik object */
  const getFuelInitialValues = () => {
    let arr: any[] = [];
    if (FuelSetting && FuelSetting.length > 0) {
      arr = [...FuelSetting];
      let distinctFuelSetting: any = [];
      FuelSetting && FuelSetting.forEach((fuel: any) => {
        const existing = distinctFuelSetting.find((item: any) => item.fuel_type === fuel.fuel_type &&
          item.vessel_machinery_fc_group === fuel.vessel_machinery_fc_group);
        // If not found, push the new fuel class
        if (!existing) {
          distinctFuelSetting.push(fuel);
        }
      });
      VesselMachinery && VesselMachinery?.flatMap((machine: any) => {
        FuelTypes?.filter((item: any) =>
          !distinctFuelSetting.some((fuel: any) => item?.fuel_type === fuel?.fuel_type &&
            machine?.id === fuel?.vessel_machinery_fc_group)
        )?.forEach((fuelType: any) => {
          arr.push({
            vessel: VesselID,
            vessel_machinery_fc_group: machine.id,
            vessel_machinery_fc_group_name: machine.vessel_machinery_fc_group_name,
            fuel_type: fuelType.fuel_type,
            selected: false,
          })
        })
        return '';
      })
      return arr;
    } else if (VesselMachinery && FuelTypes) {
      const FuelConsumption = VesselMachinery.flatMap((machine: any) =>
        FuelTypes.map((fuel: any) => ({
          vessel: VesselID,
          vessel_machinery_fc_group: machine.id,
          vessel_machinery_fc_group_name: machine.vessel_machinery_fc_group_name,
          fuel_type: fuel.fuel_type,
          selected: false,
        }))
      );
      return FuelConsumption;
    }
  };

  /** General Settings Formik Object */
  const FuelFormik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      fuel: getFuelInitialValues()
    },
    onSubmit: async () => {
      FuelFormik.setSubmitting(true);
      let responseArray: any[] = [];
      FuelFormik?.values.fuel.forEach((val: any) => {
        if (val?.id > 0) {
          if (val?.selected === false) {
            responseArray.push(apiGlobal.delete(`/vessel_fuel_setting/${val.id}/`));
          }
        } else {
          if (val?.selected === true) {
            responseArray.push(apiGlobal.post(`/vessel_fuel_setting/`, val));
          }
        }
      })
      /** handle server response */
      await handleServerResponse(responseArray).then(async (res) => {
        if (res) {
          await queryClient.invalidateQueries(queryKeyes.vessel.FuelSettings.key);
          await queryClient.invalidateQueries(queryKeyes.vessel.ConfigurationList.key);
          setRefreshVesselMachineries(refreshVesselMachineries + 1);
          dispatch(getConfigurationLimitsAction(VesselID));
        }
      });
      FuelFormik.setSubmitting(false);
    }
  });

  const tabName = 'Machinery Fuel';
  const handleFormikStateChange = (values: any, initialValues: any) => {
    const areValuesEqual = isEqual(values, initialValues);;
    setCheckValuesBeforeSwitch(areValuesEqual);
    setTabName(tabName);
    return areValuesEqual;
  };

  useEffect(() => {
    handleFormikStateChange(FuelFormik?.values, FuelFormik?.initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [FuelFormik?.values, FuelFormik?.initialValues]);

  /** useEffect */
  useEffect(() => {
    FuelFormik.initialValues.fuel = getFuelInitialValues();
    FuelFormik.values.fuel = getFuelInitialValues();
    setRefreshVesselMachineries(refreshVesselMachineries + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [FuelTypes, VesselMachinery, VesselID])

  useEffect(() => {
    if (VesselConfActiveTab === VesselConfigrationTabs.MACHINERY_FUEL && FuelFormik?.initialValues !== FuelFormik?.values) {
      setFormik(FuelFormik); // Example: Assign formik instance for the "Other" tab
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [VesselConfActiveTab, FuelFormik]);

  if (reportingLoading) {
    return <FormLoading />
  } else if (reportingError) {
    return <ErrorComponent message='Error loading required data!' />
  } else {
    return (
      <>
        <Card className="border-0">
          <h4 className="mb-3 navbar_menu">Machinery Fuel Settings</h4>
          {(reportingLoading) && (
            <FormLoading />
          )}
          {(reportingError) && (
            <ErrorComponent message="Error loading required data!" />
          )}
          {/* {FuelSettingError && FuelFormik.initialValues=getFuelInitialValues()} */}
          {!(reportingLoading) &&
            !(reportingError) &&
            <Form
              onSubmit={FuelFormik.handleSubmit}
              noValidate
              autoComplete="off"
            >
              <CardBody className="p-0">
                <Row>
                  <Col md={12} className="px-0">
                    <div className="table-responsive p-0">
                      <table className="table mb-2" key={`${refreshForm}-${refreshVesselMachineries}`}>
                        <thead className="table-light">
                          <tr>
                            <th className="p-2 align-middle sr-no-width">
                              #
                            </th>
                            <th className="p-2 align-middle">
                              Machinery name
                            </th>
                            {FuelTypes.length > 0 &&
                              FuelTypes.map(
                                (fuel_type: any, index: number) => (
                                  <th
                                    key={fuel_type.id || index}
                                    className="p-2 align-middle text-center"
                                    style={{ width: "13%" }}
                                    id={`Fuel_Type_${fuel_type.id}`}
                                    onMouseEnter={() => setHoverId(`Fuel_Type_${fuel_type.id}`)}
                                    onMouseLeave={() => setHoverId(null)}
                                  >
                                    {fuel_type.fuel_type_name}
                                    <i className="bx bx-info-circle ml-2p" />
                                  </th>
                                )
                              )}
                          </tr>
                        </thead>
                        <tbody>
                          {(!VesselMachinery || VesselMachinery.length === 0) ? (
                            <tr>
                              <td colSpan={FuelTypes.length + 2} className="text-center">
                                <Message message="No vessel machinery available." type="warning" width="30rem" />
                              </td>
                            </tr>
                          ) : (
                            VesselMachinery.map((machine: any, machineIndex: number) => {
                              const filteredFuels = FuelFormik.values.fuel?.filter(
                                (fuel: any) => fuel.vessel_machinery_fc_group === machine.id
                              );

                              return (
                                <tr key={machine.id || machineIndex}>
                                  <td className="p-2 align-middle text-center">{machineIndex + 1}</td>
                                  <td className="p-2 align-middle">
                                    {machine.vessel_machinery_fc_group_name}
                                  </td>
                                  {FuelTypes.map((fuelType: any, fuelIndex: number) => {
                                    const fuelEntry = filteredFuels?.find(
                                      (fuel: any) => fuel.fuel_type === fuelType.fuel_type
                                    );

                                    const fieldName = `fuel.${FuelFormik?.values?.fuel?.indexOf(
                                      fuelEntry || {}
                                    )}.selected`;

                                    return (
                                      <td key={fuelIndex} className="p-2 align-middle text-center">
                                        <Input
                                          onFocus={(e) => e.target.select()}
                                          type="checkbox"
                                          id={`${machine.id}_${fuelType.id}`}
                                          name={fieldName}
                                          onBlur={FuelFormik.handleBlur}
                                          onChange={(e: any) => {
                                            FuelFormik.handleChange(e);
                                          }}
                                          defaultChecked={fuelEntry?.selected || false}
                                          disabled={isConfigurationButtonDisabled(vessel)}
                                        />
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </Col>
                </Row>
              </CardBody>
              <CardFooter className="p-2 py-3 mb-3">
                <Button
                  type="submit"
                  color="primary"
                  className="justify_right"
                  disabled={isConfigurationButtonDisabled(vessel) || FuelFormik?.isSubmitting}
                >
                  {FuelFormik?.isSubmitting && <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />}
                  Save
                </Button>
              </CardFooter>
              <Row className="mt-2">
                <FormValuesDebug
                  values={[
                    FuelFormik.values,
                    FuelFormik.errors,
                    FuelFormik.initialValues,
                  ]}
                />
              </Row>
            </Form>}
        </Card>
        {hoverId !== null &&
          <ToolTip
            target={hoverId}
            message={formattooltip(hoverId)}
            isOpen={hoverId !== null}
          />
        }
        <p>- Set all types of fuels used by the machinery.</p>
      </>
    )
  };
};

export default FuelMachineryConfiguration;
